import React, { useRef, useEffect, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { XROrigin } from '@react-three/xr';
import * as THREE from 'three';
import { useStore } from '../store';
import { ZONES } from '../config/zones';
import { shared } from './shared';

function useKeyboard() {
  const keys = useRef({});
  useEffect(() => {
    const down = (e) => (keys.current[e.code] = true);
    const up = (e) => (keys.current[e.code] = false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);
  return keys;
}

// Matte-white + gold 'objective observer' fleet.
function SUV() {
  const white = <meshStandardMaterial color="#f2f2ee" metalness={0.2} roughness={0.45} flatShading />;
  return (
    <group>
      <mesh position={[0, 1.1, 0]} castShadow>
        <boxGeometry args={[2.4, 1, 4.6]} />
        {white}
      </mesh>
      <mesh position={[0, 1.85, -0.3]} castShadow>
        <boxGeometry args={[2.0, 0.9, 2.4]} />
        <meshStandardMaterial color="#fbfbf7" metalness={0.2} roughness={0.4} />
      </mesh>
      <mesh position={[0, 1.85, 0.95]}>
        <boxGeometry args={[1.9, 0.7, 0.1]} />
        <meshStandardMaterial color="#7dd3fc" emissive="#2ec5ff" emissiveIntensity={0.3} transparent opacity={0.55} />
      </mesh>
      <mesh position={[0, 1.5, 0]}>
        <boxGeometry args={[2.46, 0.1, 4.66]} />
        <meshStandardMaterial color="#D4AF37" metalness={0.9} roughness={0.2} emissive="#D4AF37" emissiveIntensity={0.35} />
      </mesh>
      {[[-1.15, 1.6], [1.15, 1.6], [-1.15, -1.6], [1.15, -1.6]].map((w, i) => (
        <mesh key={i} position={[w[0], 0.55, w[1]]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.55, 0.55, 0.4, 12]} />
          <meshStandardMaterial color="#141414" roughness={0.8} />
        </mesh>
      ))}
      {/* Headlamp lenses */}
      {[-0.7, 0.7].map((x, i) => (
        <mesh key={i} position={[x, 1.0, 2.35]}>
          <sphereGeometry args={[0.16, 8, 8]} />
          <meshStandardMaterial color="#fff6da" emissive="#fff6da" emissiveIntensity={1.4} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}

function Helicopter({ rotorRef }) {
  const white = <meshStandardMaterial color="#f2f2ee" metalness={0.25} roughness={0.4} flatShading />;
  return (
    <group>
      <mesh castShadow position={[0, 0, 0.4]}>
        <capsuleGeometry args={[1.5, 2.2, 6, 10]} />
        {white}
      </mesh>
      <mesh position={[0, 0.3, 1.6]}>
        <sphereGeometry args={[1.1, 12, 10]} />
        <meshStandardMaterial color="#8fd0ff" emissive="#2ec5ff" emissiveIntensity={0.25} transparent opacity={0.55} />
      </mesh>
      {/* Tail boom */}
      <mesh position={[0, 0.3, -3.2]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.22, 0.4, 4, 8]} />
        {white}
      </mesh>
      <mesh position={[0, 0.9, -5]}>
        <boxGeometry args={[0.12, 1.2, 0.6]} />
        <meshStandardMaterial color="#D4AF37" metalness={0.9} roughness={0.2} emissive="#D4AF37" emissiveIntensity={0.3} />
      </mesh>
      {/* gold trim ring */}
      <mesh position={[0, -0.9, 0.4]}>
        <boxGeometry args={[3.2, 0.12, 0.5]} />
        <meshStandardMaterial color="#D4AF37" metalness={0.9} roughness={0.2} emissive="#D4AF37" emissiveIntensity={0.3} />
      </mesh>
      {/* Main rotor */}
      <group ref={rotorRef} position={[0, 2.1, 0.4]}>
        {[0, Math.PI / 2].map((r, i) => (
          <mesh key={i} rotation={[0, r, 0]}>
            <boxGeometry args={[11, 0.08, 0.5]} />
            <meshStandardMaterial color="#dcdcd6" transparent opacity={0.8} />
          </mesh>
        ))}
      </group>
      {/* Skids */}
      {[-1.1, 1.1].map((x, i) => (
        <mesh key={i} position={[x, -1.5, 0.4]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.1, 0.1, 4, 6]} />
          <meshStandardMaterial color="#cfcfc9" />
        </mesh>
      ))}
    </group>
  );
}

function Boat() {
  const white = <meshStandardMaterial color="#f4f4f0" metalness={0.15} roughness={0.4} flatShading />;
  return (
    <group>
      <mesh castShadow position={[0, 0.5, 0]}>
        <boxGeometry args={[3, 0.9, 6.5]} />
        {white}
      </mesh>
      {/* Prow */}
      <mesh position={[0, 0.5, 3.6]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[1.5, 2, 4]} />
        {white}
      </mesh>
      <mesh position={[0, 1.3, -0.8]}>
        <boxGeometry args={[2.2, 1, 2.4]} />
        <meshStandardMaterial color="#fbfbf7" metalness={0.2} roughness={0.4} />
      </mesh>
      <mesh position={[0, 1.02, 0]}>
        <boxGeometry args={[3.1, 0.12, 6.6]} />
        <meshStandardMaterial color="#D4AF37" metalness={0.9} roughness={0.2} emissive="#D4AF37" emissiveIntensity={0.35} />
      </mesh>
    </group>
  );
}

export default function Rig() {
  const keys = useKeyboard();
  const { camera } = useThree();
  const suvRef = useRef();
  const heliRef = useRef();
  const boatRef = useRef();
  const rotorRef = useRef();
  const headRef = useRef();
  const originRef = useRef();

  const pos = useRef(new THREE.Vector3(0, 1.4, 30));
  const yaw = useRef(Math.PI); // face toward -z (river) initially
  const cued = useRef(false); // one-time "use the bridge" cue when SUV first hits water
  const throttle = useRef(0);

  const tmpCam = useMemo(() => new THREE.Vector3(), []);
  const tmpLook = useMemo(() => new THREE.Vector3(), []);

  useEffect(() => {
    camera.position.set(0, 6, 44);
    camera.lookAt(0, 3, -20);
  }, [camera]);

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.05);
    const mode = useStore.getState().mode;

    // Cinematic photo mode — fixed dramatic poses for hero captures.
    if (shared.photo) {
      if (suvRef.current) suvRef.current.visible = false;
      if (rotorRef.current) rotorRef.current.rotation.y += dt * 26;
      if (shared.photo === 'faultline') {
        if (boatRef.current) { boatRef.current.visible = true; boatRef.current.position.set(-40, 0, 4); boatRef.current.rotation.y = Math.PI / 2; }
        if (heliRef.current) heliRef.current.visible = false;
        camera.position.set(-56, 3.4, -1);
        camera.lookAt(30, 6.5, 3);
      } else if (shared.photo === 'aerial') {
        if (heliRef.current) heliRef.current.visible = false;
        if (boatRef.current) boatRef.current.visible = false;
        camera.position.set(-46, 44, 104);
        camera.lookAt(34, 12, -12);
      }
      shared.camPos.copy(camera.position);
      shared.camDir.set(0, 0, -1).applyQuaternion(camera.quaternion);
      return;
    }

    const tourActive = useStore.getState().tourActive;
    const currentZone = useStore.getState().currentZone;
    const bankFocus = useStore.getState().bankFocus;
    const presenting = state.gl.xr?.isPresenting;

    const isHeli = mode === 'helicopter';
    const isBoat = mode === 'boat';
    const groundY = 1.4;

    if (tourActive) {
      // Cinematic auto-tour: hover over the river at the active zone.
      const zone = ZONES[currentZone] || ZONES[0];
      pos.current.x = THREE.MathUtils.lerp(pos.current.x, zone.x, dt * 1.5);
      const camHeight = isHeli ? 66 : 34;
      const lookZ = bankFocus === 'progressive' ? -34 : bankFocus === 'traditional' ? 34 : 0;
      tmpCam.set(zone.x, camHeight, 96);
      tmpLook.set(zone.x, 8, lookZ);

      shared.eagleTarget.set(zone.x + 8, isHeli ? 52 : 24, 58);
      shared.lookTarget.set(zone.x, 6, 0);
    } else {
      // Free navigation (keyboard + touch joystick)
      const k = keys.current;
      const jx = shared.input;
      const turn = 1.5 * dt;
      if (k['KeyA'] || k['ArrowLeft']) yaw.current += turn;
      if (k['KeyD'] || k['ArrowRight']) yaw.current -= turn;
      yaw.current += turn * jx.turn;

      const fwd = new THREE.Vector3(Math.sin(yaw.current), 0, Math.cos(yaw.current));
      const speed = (isHeli ? 46 : isBoat ? 30 : 28) * dt;
      if (k['KeyW'] || k['ArrowUp']) pos.current.addScaledVector(fwd, speed);
      if (k['KeyS'] || k['ArrowDown']) pos.current.addScaledVector(fwd, -speed);
      if (jx.forward) pos.current.addScaledVector(fwd, speed * jx.forward);

      // Right-stick camera look + left-stick strafe
      yaw.current += (jx.lookYaw || 0) * dt * 1.8;
      if (jx.strafe) {
        const rightV = new THREE.Vector3(Math.cos(yaw.current), 0, -Math.sin(yaw.current));
        pos.current.addScaledVector(rightV, speed * jx.strafe);
      }

      if (isHeli) {
        if (k['KeyE'] || k['Space']) pos.current.y += 30 * dt;
        if (k['KeyQ'] || k['ShiftLeft']) pos.current.y -= 30 * dt;
        if (jx.up) pos.current.y += 30 * dt * jx.up;
        pos.current.y = THREE.MathUtils.clamp(pos.current.y, 22, 95);
      } else if (isBoat) {
        pos.current.y = 0.6;
      } else {
        pos.current.y = groundY;
      }

      // keep inside the world bounds; the boat stays in the neutral river channel
      pos.current.x = THREE.MathUtils.clamp(pos.current.x, -210, 210);
      if (isBoat) {
        pos.current.z = THREE.MathUtils.clamp(pos.current.z, -11, 11);
      } else {
        pos.current.z = THREE.MathUtils.clamp(pos.current.z, -125, 125);
        // SUV/ground: no driving on water. Must cross via the Bridge of Consensus
        // (|x| <= BRIDGE_HALF_X). Touch the river anywhere else → reset to nearest bank.
        if (!isHeli) {
          const RIVER_EDGE = 13, BRIDGE_HALF_X = 9;
          const onBridge = Math.abs(pos.current.x) <= BRIDGE_HALF_X;
          if (!onBridge && Math.abs(pos.current.z) < RIVER_EDGE) {
            const side = pos.current.z >= 0 ? 1 : -1;
            pos.current.z = side * (RIVER_EDGE + 1.5);
            if (!cued.current && !useStore.getState().narrating) {
              cued.current = true;
              useStore.getState().triggerBridgeCue();
            }
          }
        }
      }

      const camBack = isHeli ? 17 : 11;
      const camUp = isHeli ? 8 : 5.5;
      tmpCam.copy(pos.current).addScaledVector(fwd, -camBack);
      tmpCam.y = pos.current.y + camUp;
      tmpLook.copy(pos.current).addScaledVector(fwd, isHeli ? 20 : 6);
      tmpLook.y = pos.current.y + (isHeli ? -4 : 1.5) + (jx.lookPitch || 0) * 8;

      // Eagle leads ahead of the traveller
      shared.eagleTarget.copy(pos.current).addScaledVector(fwd, 18);
      shared.eagleTarget.y = pos.current.y + (isHeli ? 6 : 10);
      shared.lookTarget.copy(pos.current).addScaledVector(fwd, 30);
      // Expose vehicle position for automated drive tests
      shared.vehicleX = pos.current.x;
      shared.vehicleZ = pos.current.z;
    }

    if (presenting && originRef.current) {
      // In VR, move the XR origin to a comfortable vantage; user looks around freely.
      const target = tourActive ? new THREE.Vector3((ZONES[currentZone] || ZONES[0]).x, 0, 60) : new THREE.Vector3(pos.current.x, 0, pos.current.z);
      originRef.current.position.lerp(target, dt * 2);
      originRef.current.rotation.y = Math.PI + shared.snapYaw;
    } else {
      camera.position.lerp(tmpCam, Math.min(1, dt * (tourActive ? 1.4 : 4)));
      camera.lookAt(tmpLook);
    }

    // Fleet visibility + follow
    const showVehicle = !tourActive;
    if (suvRef.current) {
      suvRef.current.visible = showVehicle && mode === 'suv';
      suvRef.current.position.set(pos.current.x, 0, pos.current.z);
      suvRef.current.rotation.y = yaw.current;
    }
    if (heliRef.current) {
      heliRef.current.visible = showVehicle && isHeli;
      heliRef.current.position.set(pos.current.x, pos.current.y - 2.2, pos.current.z);
      heliRef.current.rotation.y = yaw.current;
    }
    if (boatRef.current) {
      boatRef.current.visible = showVehicle && isBoat;
      boatRef.current.position.set(pos.current.x, 0, pos.current.z);
      boatRef.current.rotation.y = yaw.current;
    }
    if (rotorRef.current) rotorRef.current.rotation.y += dt * 26;
    if (headRef.current) headRef.current.intensity = (showVehicle && mode === 'suv') ? (0.3 + shared.env.night * 2.4) : 0;

    // Publish camera transform for the binaural audio listener
    shared.camPos.copy(camera.position);
    shared.camDir.set(0, 0, -1).applyQuaternion(camera.quaternion);

    // Publish transform to the HUD minimap (throttled)
    throttle.current += dt;
    if (throttle.current > 0.1) {
      throttle.current = 0;
      useStore.getState().setPlayerTransform(pos.current.x, pos.current.z, yaw.current);
    }
  });

  return (
    <group>
      <group ref={originRef}>
        <XROrigin />
      </group>
      <group ref={suvRef}>
        <SUV />
        <pointLight ref={headRef} position={[0, 1.4, 3.4]} color="#fff6da" intensity={0.4} distance={45} />
      </group>
      <group ref={heliRef} visible={false}>
        <Helicopter rotorRef={rotorRef} />
      </group>
      <group ref={boatRef} visible={false}>
        <Boat />
      </group>
    </group>
  );
}
